<template>
  <div class="time-sheets-actions">
    <div class="left">
      <BulkActionDropdown
        v-if="showBulkAction"
        :menu-items="[
          {
            icon: 'check',
            label: this.$t('timeSheets.listing.labels.approveTimeSheets'),
            clickaction: 'APPROVE'
          },
          {
            icon: 'times',
            label: this.$t('timeSheets.listing.labels.rejectTimeSheets'),
            clickaction: 'REJECT'
          },
          { icon: 'copy', label: this.$t('timeSheets.listing.labels.bulkPdf'), clickaction: 'PDF' }
        ]"
        @bulk-action-selected="onBulkActionSelected($event)"
        @all-none-selected="selectOrDeselectTimeSheets($event)"
      />
      <div class="listing-title">{{ $t(titleAllOrFiltered) }}</div>
    </div>
    <div class="search">
      <tol-type-ahead
        :search-params="{
          url: `/time-sheets`,
          searchKeys: ['searchParam'],
          urlParams: {
            ...getTimeSheetsSortOrder,
            recordsPerPage: TimeSheetsPerPage,
            offset: TimeSheetsListingOffset
          }
        }"
        :results-in-dropdown="false"
        :allow-empty-search="true"
        @filtered="onFiltered($event)"
      >
      </tol-type-ahead>
    </div>
    <div class="export-to-excel"></div>
    <TolAltButton
      :tooltip="$t('expenses.listing.actions.exportToXlsx')"
      label="Export to excel"
      icon-name="file-excel"
      @click="exportAsExcel"
    />
    <ConfirmModal
      v-if="showConfirmModal"
      :title="selectedBulkAction"
      :action="selectedBulkAction"
      @continue-action="continueBulkAction($event)"
      @cancel-action="cancelBulkAction()"
    >
      <template v-slot:modalData>
        {{ $t('bulk.labels.totalSelected') }}: {{ selectedItems.length }}
      </template>
      <template v-slot:modalMessage>
        {{ $t('bulk.dialogs.approveRejectConfirmationDialog.confirmMessage', { message }) }}
      </template>
    </ConfirmModal>
    <VueHtml2pdf
      ref="BatchDownloadComp"
      :show-layout="false"
      :paginate-elements-by-height="1400"
      pdf-format="a4"
      :manual-pagination="false"
      :enable-download="false"
      :preview-modal="true"
      :filename="`Timesheet-Batch`"
    >
      <section slot="pdf-content">
        <TimeSheetBatchPdf @readyToExport="exportAsPdf" />
      </section>
    </VueHtml2pdf>
  </div>
</template>

<script>
import TolAltButton from '@/app/components/TolAltButton';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import {
  TimeSheetsListingActions,
  TimeSheetsListingGetters
} from '@/app/features/time-sheets/features/time-sheets-listing/time-sheets-listing.keys';
import BulkActionDropdown from '@/app/components/BulkActionDropdown.vue';
import ConfirmModal from '@/app/components/ConfirmModal.vue';
import { mapGetters } from 'vuex';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import TimeSheetBatchPdf from '../../time-sheet-details/components/TimeSheetBatchPdf.vue';

export default {
  name: 'TimeSheetsActions',
  components: {
    TolTypeAhead,
    TolAltButton,
    ConfirmModal,
    BulkActionDropdown,
    TimeSheetBatchPdf
  },
  props: {
    titleAllOrFiltered: {
      type: String,
      default: 'timeSheets.listing.actions.titleAll'
    },
    showBulkAction: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showConfirmModal: false,
      selectedBulkAction: ''
    };
  },
  computed: {
    ...mapGetters([
      TimeSheetsListingGetters.TimeSheetsPerPage,
      TimeSheetsListingGetters.TimeSheetsFilteredSortedRecordCount,
      TimeSheetsListingGetters.TimeSheetsListingOffset,
      TimeSheetsListingGetters.TimeSheetsSearchParam,
      TimeSheetsListingGetters.GetTimeSheetsSortOrder
    ]),
    isApprover: {
      get() {
        return this.$store.state.currentUser.isApprover;
      }
    },
    anyItemsSelected() {
      return this.$store.getters.SelectedTimeSheets('referenceNumber').length > 0;
    },
    selectedItems() {
      return this.$store.getters.SelectedTimeSheets('referenceNumber');
    },
    message: {
      get() {
        let res = '';
        switch (this.selectedBulkAction) {
          case 'APPROVE':
            res = this.$t('bulk.actions.approve');
            break;
          case 'REJECT':
            res = this.$t('bulk.actions.reject');
            break;
          case 'PDF':
            res = this.$t('bulk.actions.saveAsPdf');
            break;
        }
        return res;
      }
    }
  },
  methods: {
    onFiltered(results) {
      let searchedTimeSheets = results.data;
      let searchKey = results.searchParam;
      this.$store.dispatch(TimeSheetsListingActions.UpdateTimeSheetsSearchParam, {
        timeSheets: searchedTimeSheets,
        searchParam: searchKey
      });
    },
    exportAsExcel() {
      this.$store.dispatch(TimeSheetsListingActions.ExportTimeSheetsAsExcel);
    },
    exportAsPdf() {
      this.$refs.BatchDownloadComp.generatePdf();
    },
    onBulkActionSelected(bulkAction) {
      this.selectedBulkAction = bulkAction;
      if (this.canDoBulkAction(bulkAction)) this.showConfirmModal = this.anyItemsSelected;
      else {
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: this.$t('bulk.errors.approveRejectDisallowed')
        });
      }
      this.$store.dispatch(TimeSheetsListingActions.SetTimeSheetsBulkAction, bulkAction);
    },
    continueBulkAction(event) {
      this.showConfirmModal = false;
      this.$store.dispatch(TimeSheetsListingActions.DoTimeSheetsBulkAction, event);
    },
    cancelBulkAction() {
      this.showConfirmModal = false;
      this.$store.dispatch(TimeSheetsListingActions.SelectDeselectAllTimeSheets, {
        clicked: false
      });
    },
    selectOrDeselectTimeSheets(event) {
      this.$store.dispatch(TimeSheetsListingActions.SelectDeselectAllTimeSheets, event);
    },
    canDoBulkAction(bulkAction) {
      const selectedTimeSheetStatuses = this.$store.getters.SelectedTimeSheets('status');
      switch (bulkAction) {
        case 'APPROVE':
          return selectedTimeSheetStatuses.every(
            status => status === 'SUBMITTED' || status === 'REJECTED'
          );
        case 'REJECT':
          return selectedTimeSheetStatuses.every(
            status => status === 'SUBMITTED' || status === 'APPROVED'
          );
        case 'PDF':
          return true;
        default:
          return false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/colors.scss';
@import '../../../../../styles/variables.scss';

.time-sheets-actions {
  display: flex;
  flex-direction: row;
  background-color: $background-level-2;
  padding: 60px;

  .left {
    display: flex;
    flex-basis: 20%;
    .listing-title {
      color: $text-color-dark;
      font-size: 21px;
      font-weight: bold;
      letter-spacing: -0.24px;
      line-height: 28px;
      padding-top: 10px;
    }
  }

  .search {
    flex-basis: 60%;
  }

  .timesheets-search {
    display: flex;
    flex-direction: row;
    background-color: $background-level-2;
    flex-basis: 60%;

    .search {
      padding-top: 0px;
      margin-right: 30px;
      width: 100%;
    }
  }

  .export-to-excel {
    flex-basis: 20%;
    .wrapper {
      justify-content: flex-end;
    }
  }
}
</style>
